import { useCallback, useEffect, useState } from "react";
import { PermissionStatusEnum, useMicPermission } from "./useMicPermission";
import { appLogger } from "@/utils/AppLogger";
import { MicrophoneError, MicrophoneErrorEnum } from "../types";

export type MediaDevice = MediaDeviceInfo & {
  value: string;
};

export const useMicList = () => {
  const [devices, setDevices] = useState<MediaDevice[]>([]);
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<MicrophoneError | null>(null);

  const { verifyMicPermission, askMicPermission } = useMicPermission();

  const onConnectMicrophone = useCallback(async () => {
    const status = await verifyMicPermission();
    if (status !== PermissionStatusEnum.GRANTED) {
      return await askMicPermission();
    }
    return status;
  }, [verifyMicPermission, askMicPermission]);

  const getMicrophoneDevices = useCallback(async () => {
    const allDevices = await navigator.mediaDevices.enumerateDevices();
    const audioInputs = allDevices
      .filter((device): device is MediaDeviceInfo => device.kind === "audioinput")
      .map((device, index) => {
        const label = device.label || `Device ${index + 1}`;
        const deviceId = device.deviceId || `${index}`;

        return {
          ...device,
          label,
          deviceId,
          value: deviceId,
        } as MediaDevice;
      });

    return audioInputs;
  }, []);

  const onUpdateDevices = useCallback(async () => {
    try {
      const audioInputs = await getMicrophoneDevices();
      if (audioInputs.length) {
        setDevices(audioInputs);
      } else {
        setDevices([{ label: "No devices found", value: "no-device" } as MediaDevice]);
      }
      return audioInputs;
    } catch (error) {
      appLogger(`[MICROPHONE] Error on devices list update - ${error}`, { type: "error" });
    }
  }, [getMicrophoneDevices]);

  const startDevicesListener = useCallback(async () => {
    setIsPending(true);
    setError(null);
    try {
      const status = await onConnectMicrophone();
      if (status === PermissionStatusEnum.GRANTED) {
        setError(null);
        await onUpdateDevices();
        navigator.mediaDevices.addEventListener("devicechange", onUpdateDevices);
        return () => navigator.mediaDevices.removeEventListener("devicechange", onUpdateDevices);
      } else if (status === PermissionStatusEnum.DENIED) {
        setError(MicrophoneErrorEnum.PERMISSION_DENIED);
        setDevices([{ label: "No devices found", value: "no-device" } as MediaDevice]);
      }
    } finally {
      setIsPending(false);
    }
  }, [onConnectMicrophone, onUpdateDevices]);

  useEffect(() => {
    let cleanup: (() => void) | undefined;
    const init = async () => {
      cleanup = await startDevicesListener();
    };
    init();

    return () => {
      if (cleanup) {
        cleanup();
      }
    };
  }, [startDevicesListener]);

  return { error, devices, isPending, startDevicesListener, getMicrophoneDevices };
};
